import { SVGProps, useMemo } from 'react';

import { useDayJs } from '@common/hooks';
import { withImageOptimizationQuery } from '@common/utils';
import {
  GetPdpBaseInfo_catalog_product_base_info_seller_notice,
  IconNoticeDelivery,
  IconNoticeNoti,
  IconNoticeVacation,
  PdpCatalogFulfillmentType,
  useCatalogProductIdParams,
  usePdpBaseInfo,
} from '@domains/pdp-common';

import { GetNativeDescription_pdp_description_content_seller_notice } from '../../product-description/graphql/native-description_types.2';

type SellerNoticeType =
  | GetNativeDescription_pdp_description_content_seller_notice
  | GetPdpBaseInfo_catalog_product_base_info_seller_notice;

type VacationNoticeType = SellerNoticeType & {
  date_holiday_started?: number;
  date_holiday_ended?: number;
};

export interface Notice {
  title: string;
  content: string;
  imageUrl: string;
  date_holiday_started?: number;
  date_holiday_ended?: number;
}

interface SellerNoticeBaseProps {
  seller_notice: SellerNoticeType | null;
  is_zigzin_delivery?: boolean;
}

interface SellerNoticeServiceResult {
  notice: Notice;
  noticeIcon: ({ width, height, color, ...props }: SVGProps<SVGSVGElement>) => JSX.Element;
  headerTitle: string;
  notice_type: string;
  titleDate: string;
  date: string;
  isImageType: boolean;
  isHide: boolean;
}

const isVacationNotice = (notice: SellerNoticeType | null): notice is VacationNoticeType => {
  return notice?.notice_type === 'VACATION' && 'date_holiday_started' in notice && 'date_holiday_ended' in notice;
};

const createDateFormatter = (dayjs: ReturnType<typeof useDayJs>) => ({
  formatTitleDate: (start: number, end: number) =>
    `${dayjs(start).format('M. D(ddd)')} - ${dayjs(end).format('M. D(ddd)')}`,

  formatDetailDate: (start: number, end: number) =>
    `${dayjs(start).format('YY. MM. DD(ddd)')} - ${dayjs(end).format('YY. MM. DD(ddd)')}`,
});

// 휴가 날짜 포맷팅 로직 분리
const formatVacationDates = (
  notice: SellerNoticeType | null,
  dateFormatter: ReturnType<typeof createDateFormatter>,
) => {
  if (!isVacationNotice(notice) || !notice.date_holiday_started || !notice.date_holiday_ended) {
    return { titleDate: '', date: '' };
  }

  return {
    titleDate: dateFormatter.formatTitleDate(notice.date_holiday_started, notice.date_holiday_ended),
    date: dateFormatter.formatDetailDate(notice.date_holiday_started, notice.date_holiday_ended),
  };
};

// 공지 타입별 메타데이터
const NOTICE_META = {
  VACATION: { title: '휴가공지', icon: IconNoticeVacation },
  DELIVERY: { title: '배송공지', icon: IconNoticeDelivery },
  NORMAL: { title: '공지사항', icon: IconNoticeNoti },
} as const;

export const useSellerNoticeBase = ({
  seller_notice,
  is_zigzin_delivery = false,
}: SellerNoticeBaseProps): SellerNoticeServiceResult => {
  const dayjs = useDayJs();

  const dateFormatter = useMemo(() => createDateFormatter(dayjs), [dayjs]);

  const notice_type = seller_notice?.notice_type ?? 'NORMAL';
  const meta = NOTICE_META[notice_type as keyof typeof NOTICE_META] ?? NOTICE_META.NORMAL;

  const { titleDate, date } = useMemo(
    () => formatVacationDates(seller_notice, dateFormatter),
    [seller_notice, dateFormatter],
  );

  const notice = useMemo(
    (): Notice => ({
      title: seller_notice?.title ?? '',
      content: seller_notice?.content ?? '',
      imageUrl:
        withImageOptimizationQuery(seller_notice?.image_url, { format: 'webp', quality: 80 }) ??
        seller_notice?.image_url ??
        '',
      date_holiday_started: isVacationNotice(seller_notice) ? seller_notice.date_holiday_started : undefined,
      date_holiday_ended: isVacationNotice(seller_notice) ? seller_notice.date_holiday_ended : undefined,
    }),
    [seller_notice],
  );

  return {
    notice,
    noticeIcon: meta.icon,
    headerTitle: meta.title,
    notice_type,
    titleDate,
    date,
    isImageType: notice_type === 'IMAGE' && Boolean(seller_notice?.image_url),
    isHide: !seller_notice || is_zigzin_delivery,
  };
};

export const useSellerNotice = (): SellerNoticeServiceResult => {
  const param = useCatalogProductIdParams();
  const {
    product: { fulfillment_type },
    seller_notice,
  } = usePdpBaseInfo(param);

  const is_zigzin_delivery = useMemo(
    () =>
      fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN &&
      ['DELIVERY', 'VACATION'].includes(seller_notice?.notice_type ?? ''),
    [fulfillment_type, seller_notice?.notice_type],
  );

  return useSellerNoticeBase({ seller_notice, is_zigzin_delivery });
};
